import React, { useState, useEffect, useRef, useCallback } from "react";
import { activityLogsAPI, ActivityLog } from "../services/api";
import { Loader } from "../ui/components/Loader";

interface ActivityLogComponentProps {
  feature: string;
  featureId: string | number;
}

const ActivityLogComponent: React.FC<ActivityLogComponentProps> = ({
  feature,
  featureId,
}) => {
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const limit = 10;

  const logsEndRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Fetch initial logs
  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await activityLogsAPI.fetchActivityLogs(
        feature,
        featureId,
        limit,
        0
      );

      setLogs(response.data.logs);
      setHasMore(response.data.pagination.has_more);
      setOffset(limit);
    } catch (err) {
      console.error("Error fetching activity logs:", err);
      setError("Failed to load activity logs");
    } finally {
      setLoading(false);
    }
  }, [feature, featureId]);

  // Fetch more logs for pagination
  const fetchMoreLogs = useCallback(async () => {
    if (!hasMore || loadingMore) return;

    try {
      setLoadingMore(true);
      const response = await activityLogsAPI.fetchActivityLogs(
        feature,
        featureId,
        limit,
        offset
      );

      setLogs((prevLogs) => [...prevLogs, ...response.data.logs]);
      setHasMore(response.data.pagination.has_more);
      setOffset((prevOffset) => prevOffset + limit);
    } catch (err) {
      console.error("Error fetching more activity logs:", err);
    } finally {
      setLoadingMore(false);
    }
  }, [feature, featureId, offset, hasMore, loadingMore]);

  // Setup intersection observer for infinite scroll
  useEffect(() => {
    if (loading || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchMoreLogs();
        }
      },
      { threshold: 0.1 }
    );

    if (logsEndRef.current) {
      observer.observe(logsEndRef.current);
    }

    observerRef.current = observer;

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loading, hasMore, fetchMoreLogs]);

  // Fetch logs on mount or when feature/featureId changes
  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Helper function to format date with time
  const formatDateTime = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString;
      }

      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return dateString;
    }
  };

  // Helper function to get initials from name
  const getInitials = (name: string): string => {
    if (!name) return "??";
    const parts = name.split(" ");
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Helper function to get color classes based on action type
  const getActionColorClasses = (
    actionType: string
  ): { bg: string; text: string } => {
    switch (actionType.toLowerCase()) {
      case "created":
        return {
          bg: "bg-brand-100",
          text: "text-brand-600",
        };
      case "posted":
      case "updated":
        return {
          bg: "bg-success-100",
          text: "text-success-600",
        };
      case "deleted":
        return {
          bg: "bg-error-100",
          text: "text-error-600",
        };
      default:
        return {
          bg: "bg-neutral-100",
          text: "text-neutral-600",
        };
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex w-full flex-col items-start gap-6">
        <span className="text-heading-2 font-heading-2 text-default-font">
          Activity Log
        </span>
        <div className="flex w-full items-center justify-center py-8">
          <Loader />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex w-full flex-col items-start gap-6">
        <span className="text-heading-2 font-heading-2 text-default-font">
          Activity Log
        </span>
        <div className="flex w-full items-center justify-center py-8">
          <span className="text-body font-body text-error-600">{error}</span>
        </div>
      </div>
    );
  }

  // Empty state
  if (logs.length === 0) {
    return (
      <div className="flex w-full flex-col items-start gap-6">
        <span className="text-heading-2 font-heading-2 text-default-font">
          Activity Log
        </span>
        <div className="flex w-full items-center justify-center py-8">
          <span className="text-body font-body text-subtext-color">
            No activity logs found
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col items-start gap-6">
      <span className="text-heading-2 font-heading-2 text-default-font">
        Activity Log
      </span>
      <div className="flex w-full flex-col items-start gap-3 max-h-[500px] overflow-y-auto">
        {logs.map((log, index) => {
          const colorClasses = getActionColorClasses(log.action_type);
          const isLast = index === logs.length - 1;

          return (
            <div
              key={log.id}
              className={`flex w-full items-start gap-3 ${
                !isLast ? "border-b border-solid border-neutral-100 pb-3" : ""
              }`}
            >
              <div
                className={`flex h-8 w-8 flex-none items-center justify-center rounded-full ${colorClasses.bg}`}
              >
                <span
                  className={`text-caption-bold font-caption-bold ${colorClasses.text}`}
                >
                  {getInitials(log.user_name)}
                </span>
              </div>
              <div className="flex flex-col items-start gap-1">
                <span className="text-body-bold font-body-bold text-default-font">
                  {log.activity_name}
                </span>
                <span className="text-caption font-caption text-subtext-color">
                  {log.description}
                </span>
                <span className="text-caption font-caption text-neutral-400">
                  {formatDateTime(log.created_at)}
                </span>
              </div>
            </div>
          );
        })}

        {/* Loading more indicator */}
        {loadingMore && (
          <div className="flex w-full items-center justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-600"></div>
          </div>
        )}

        {/* Observer target for infinite scroll */}
        {hasMore && !loadingMore && <div ref={logsEndRef} className="h-4" />}

        {/* End of logs message */}
        {!hasMore && logs.length > 0 && (
          <div className="flex w-full items-center justify-center py-4">
            <span className="text-caption font-caption text-subtext-color">
              No more activity logs
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityLogComponent;
